import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import { 
  MessageCircle, Bot, Gamepad2, DollarSign, Wrench, Mail, 
  Rss, Calendar, Users, Mic, MicOff, Volume2, Loader2, StopCircle,
  CheckSquare, FileText, Plus, Edit2, X, Check, Clock, ChevronDown,
  Sparkles, Send, Settings, Pause, Play, Trash2, Save, Square
} from 'lucide-react';

// AI Models for voice assistant
const VOICE_MODELS = [
  { id: 'sage', name: 'Sage', description: 'General assistant' },
  { id: 'tasks', name: 'Task Manager', description: 'Create & manage tasks' },
  { id: 'notes', name: 'Note Taker', description: 'Quick notes' },
  { id: 'calendar', name: 'Calendar', description: 'Schedule events' },
  { id: 'messenger', name: 'Messenger', description: 'Send messages' }
];

export default function Home({ navigate }) {
  const { user, profile } = useAuth();
  const [stats, setStats] = useState({ postsCount: 0, upcomingEvents: 0, friendsOnline: 0 });
  const [loading, setLoading] = useState(true);
  
  // Data
  const [tasks, setTasks] = useState([]);
  const [notes, setNotes] = useState([]);
  const [events, setEvents] = useState([]);
  const [friendsList, setFriendsList] = useState([]);
  const [channels, setChannels] = useState([]);
  
  // UI State
  const [showFriends, setShowFriends] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [editingNote, setEditingNote] = useState(null);
  const [noteEditText, setNoteEditText] = useState('');
  const [showAddTask, setShowAddTask] = useState(false);
  const [showAddNote, setShowAddNote] = useState(false);
  const [showAddEvent, setShowAddEvent] = useState(false);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [newNoteContent, setNewNoteContent] = useState('');
  const [newEventTitle, setNewEventTitle] = useState('');
  const [newEventDate, setNewEventDate] = useState('');
  
  // Voice Chat State
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [voiceMessages, setVoiceMessages] = useState([]);
  const [selectedModel, setSelectedModel] = useState('sage');
  const [showModelPicker, setShowModelPicker] = useState(false);
  const [lastAction, setLastAction] = useState(null);
  const [showVoiceOptions, setShowVoiceOptions] = useState(false);
  const [pendingAudioBlob, setPendingAudioBlob] = useState(null);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioRef = useRef(null);
  const messagesEndRef = useRef(null);
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    fetchDashboardData();
    fetchTasks();
    fetchNotes();
    fetchFriends();
    fetchChannels();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [voiceMessages]);

  const fetchDashboardData = async () => {
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const { count: postsCount } = await supabase
        .from('social_posts')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', today.toISOString());

      setStats(prev => ({ ...prev, postsCount: postsCount || 0 }));
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTasks = async () => {
    try {
      const { data } = await supabase
        .from('tasks')
        .select('*')
        .eq('user_id', user?.id)
        .eq('completed', false)
        .order('created_at', { ascending: false })
        .limit(5);
      setTasks(data || []);
    } catch (error) {}
  };

  const fetchNotes = async () => {
    try {
      const { data } = await supabase
        .from('notes')
        .select('*')
        .eq('user_id', user?.id)
        .order('updated_at', { ascending: false })
        .limit(4);
      setNotes(data || []);
    } catch (error) {}
  };

  const fetchFriends = async () => {
    try {
      const { data: friendships1 } = await supabase
        .from('friendships')
        .select('friend_id')
        .eq('user_id', user?.id)
        .eq('status', 'accepted');

      const { data: friendships2 } = await supabase
        .from('friendships')
        .select('user_id')
        .eq('friend_id', user?.id)
        .eq('status', 'accepted');

      const friendIds = [
        ...(friendships1?.map(f => f.friend_id) || []),
        ...(friendships2?.map(f => f.user_id) || [])
      ];

      if (friendIds.length > 0) {
        const { data: friends } = await supabase
          .from('users')
          .select('id, username, avatar_url')
          .in('id', friendIds);
        setFriendsList(friends || []);
        setStats(prev => ({ ...prev, friendsOnline: friends?.length || 0 }));
      }
    } catch (error) {}
  };

  const fetchChannels = async () => {
    try {
      const { data } = await supabase
        .from('channels')
        .select('*')
        .limit(10);
      setChannels(data || []);
    } catch (error) {}
  };

  // Task functions
  const toggleTask = async (taskId, completed) => {
    try {
      await supabase.from('tasks').update({ completed: !completed }).eq('id', taskId);
      setTasks(prev => prev.filter(t => t.id !== taskId));
    } catch (error) {}
  };

  const createTask = async (title, dueDate = null) => {
    try {
      const { data } = await supabase
        .from('tasks')
        .insert({ user_id: user.id, title, due_date: dueDate, completed: false })
        .select()
        .single();
      if (data) setTasks(prev => [data, ...prev].slice(0, 5));
      return data;
    } catch (error) {
      return null;
    }
  };

  // Note functions
  const createNote = async (title, content) => {
    try {
      const { data } = await supabase
        .from('notes')
        .insert({ user_id: user.id, title, content, updated_at: new Date().toISOString() })
        .select()
        .single();
      if (data) setNotes(prev => [data, ...prev].slice(0, 4));
      return data;
    } catch (error) {
      return null;
    }
  };

  const updateNote = async (noteId, content) => {
    if (!content.trim()) return;
    try {
      await supabase.from('notes').update({ content, updated_at: new Date().toISOString() }).eq('id', noteId);
      setNotes(prev => prev.map(n => n.id === noteId ? { ...n, content } : n));
      setEditingNote(null);
    } catch (error) {}
  };

  // Quick add handlers
  const handleQuickAddTask = async () => {
    if (!newTaskTitle.trim()) return;
    await createTask(newTaskTitle.trim());
    setNewTaskTitle('');
    setShowAddTask(false);
  };

  const handleQuickAddNote = async () => {
    if (!newNoteContent.trim()) return;
    await createNote('Quick Note', newNoteContent.trim());
    setNewNoteContent('');
    setShowAddNote(false);
  };

  const handleQuickAddEvent = async () => {
    if (!newEventTitle.trim()) return;
    await createTask(`📅 ${newEventTitle.trim()}`, newEventDate || null);
    setNewEventTitle('');
    setNewEventDate('');
    setShowAddEvent(false);
  };

  // Message functions
  const sendDirectMessage = async (recipientId, content) => {
    try {
      const { data } = await supabase
        .from('messages')
        .insert({
          sender_id: user.id,
          receiver_id: recipientId,
          content,
          sender_username: profile?.username || 'User'
        })
        .select()
        .single();
      return data;
    } catch (error) {
      return null;
    }
  };

  const sendChannelMessage = async (channelId, content) => {
    try {
      const { data } = await supabase
        .from('messages')
        .insert({
          sender_id: user.id,
          room_id: channelId,
          content,
          sender_username: profile?.username || 'User'
        })
        .select()
        .single();
      return data;
    } catch (error) {
      return null;
    }
  };

  // Voice Recording Functions
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) audioChunksRef.current.push(event.data);
      };

      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach(track => track.stop());
        // Store blob and show options instead of auto-processing
        setPendingAudioBlob(audioBlob);
        setShowVoiceOptions(true);
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      setIsPaused(false);
    } catch (error) {
      console.error('Error starting recording:', error);
      alert('Could not access microphone. Please allow microphone permissions.');
    }
  };

  const pauseRecording = () => {
    if (mediaRecorderRef.current && isRecording && !isPaused) {
      mediaRecorderRef.current.pause();
      setIsPaused(true);
    }
  };

  const resumeRecording = () => {
    if (mediaRecorderRef.current && isRecording && isPaused) {
      mediaRecorderRef.current.resume();
      setIsPaused(false);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsPaused(false);
    }
  };

  const cancelRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stream?.getTracks().forEach(track => track.stop());
      setIsRecording(false);
      setIsPaused(false);
    }
    setPendingAudioBlob(null);
    setShowVoiceOptions(false);
  };

  const sendVoiceMessage = async () => {
    if (pendingAudioBlob) {
      setShowVoiceOptions(false);
      await processVoiceMessage(pendingAudioBlob);
      setPendingAudioBlob(null);
    }
  };

  const saveVoiceChatHistory = async () => {
    if (voiceMessages.length === 0) return;
    
    try {
      // Save to notes as a chat transcript
      const transcript = voiceMessages.map(m => 
        `[${m.role === 'user' ? 'You' : 'Sage'}]: ${m.content}`
      ).join('\n\n');
      
      await createNote(`Voice Chat - ${new Date().toLocaleDateString()}`, transcript);
      setLastAction({ type: 'note', success: true, title: 'Voice Chat Saved' });
      setTimeout(() => setLastAction(null), 2000);
    } catch (error) {
      console.error('Error saving chat history:', error);
    }
  };

  const clearVoiceChat = () => {
    setVoiceMessages([]);
    setLastAction(null);
  };

  const getSystemPromptForModel = () => {
    const basePrompt = `You are Sage, a helpful AI assistant for ${profile?.username || 'the user'}. `;
    
    switch (selectedModel) {
      case 'tasks':
        return basePrompt + `You help manage tasks. When the user wants to create a task, respond with JSON: {"action": "create_task", "title": "task title", "due_date": "YYYY-MM-DD or null"}. For other requests, respond normally.`;
      case 'notes':
        return basePrompt + `You help take notes. When the user wants to create a note, respond with JSON: {"action": "create_note", "title": "note title", "content": "note content"}. For other requests, respond normally.`;
      case 'calendar':
        return basePrompt + `You help with calendar events. When the user wants to schedule something, respond with JSON: {"action": "create_event", "title": "event title", "date": "YYYY-MM-DD", "time": "HH:MM"}. For other requests, respond normally.`;
      case 'messenger':
        return basePrompt + `You help send messages. When the user wants to send a message, respond with JSON: {"action": "send_message", "recipient": "username or channel name", "content": "message content", "type": "dm" or "channel"}. For other requests, respond normally.`;
      default:
        return basePrompt + `Keep your responses concise and conversational.`;
    }
  };

  const executeAction = async (actionData) => {
    try {
      switch (actionData.action) {
        case 'create_task':
          const task = await createTask(actionData.title, actionData.due_date);
          setLastAction({ type: 'task', success: !!task, title: actionData.title });
          return task ? `Created task: "${actionData.title}"` : 'Failed to create task';
        
        case 'create_note':
          const note = await createNote(actionData.title, actionData.content);
          setLastAction({ type: 'note', success: !!note, title: actionData.title });
          return note ? `Created note: "${actionData.title}"` : 'Failed to create note';
        
        case 'create_event':
          // For now, create as a task with due date
          const event = await createTask(`📅 ${actionData.title}`, actionData.date);
          setLastAction({ type: 'event', success: !!event, title: actionData.title });
          return event ? `Scheduled: "${actionData.title}" for ${actionData.date}` : 'Failed to create event';
        
        case 'send_message':
          if (actionData.type === 'dm') {
            const friend = friendsList.find(f => 
              f.username?.toLowerCase().includes(actionData.recipient?.toLowerCase())
            );
            if (friend) {
              const msg = await sendDirectMessage(friend.id, actionData.content);
              setLastAction({ type: 'message', success: !!msg, recipient: friend.username });
              return msg ? `Sent message to ${friend.username}` : 'Failed to send message';
            }
            return `Could not find user "${actionData.recipient}"`;
          } else {
            const channel = channels.find(c => 
              c.name?.toLowerCase().includes(actionData.recipient?.toLowerCase())
            );
            if (channel) {
              const msg = await sendChannelMessage(channel.id, actionData.content);
              setLastAction({ type: 'channel_message', success: !!msg, channel: channel.name });
              return msg ? `Sent message to #${channel.name}` : 'Failed to send message';
            }
            return `Could not find channel "${actionData.recipient}"`;
          }
        
        default:
          return null;
      }
    } catch (error) {
      console.error('Action execution error:', error);
      return 'Action failed';
    }
  };

  const processVoiceMessage = async (audioBlob) => {
    setIsProcessing(true);
    setLastAction(null);
    
    try {
      // Step 1: Transcribe audio
      const formData = new FormData();
      formData.append('file', audioBlob, 'recording.webm');
      
      const transcribeResponse = await fetch(`${backendUrl}/api/voice/transcribe`, {
        method: 'POST',
        body: formData
      });
      
      const transcribeData = await transcribeResponse.json();
      
      if (!transcribeData.success || !transcribeData.text) {
        throw new Error('Transcription failed');
      }
      
      // Add user message
      const userMessage = {
        id: Date.now(),
        role: 'user',
        content: transcribeData.text,
        timestamp: new Date().toISOString()
      };
      setVoiceMessages(prev => [...prev, userMessage]);
      
      // Step 2: Get AI response with custom system prompt
      const chatResponse = await fetch(`${backendUrl}/api/voice/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          transcribedText: transcribeData.text,
          userEmail: user?.email,
          systemPrompt: getSystemPromptForModel()
        })
      });
      
      const chatData = await chatResponse.json();
      
      if (!chatData.success) {
        throw new Error('AI response failed');
      }
      
      let aiResponseText = chatData.text;
      let actionResult = null;
      
      // Check if response contains JSON action
      try {
        const jsonMatch = aiResponseText.match(/\{[\s\S]*"action"[\s\S]*\}/);
        if (jsonMatch) {
          const actionData = JSON.parse(jsonMatch[0]);
          actionResult = await executeAction(actionData);
          if (actionResult) {
            aiResponseText = actionResult;
          }
        }
      } catch (e) {
        // Not a JSON response, use as-is
      }
      
      // Add AI message
      const aiMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: aiResponseText,
        timestamp: new Date().toISOString(),
        action: lastAction
      };
      setVoiceMessages(prev => [...prev, aiMessage]);
      
      // Step 3: Play audio response
      if (chatData.audio && !actionResult) {
        setIsSpeaking(true);
        const audio = new Audio(`data:audio/mp3;base64,${chatData.audio}`);
        audioRef.current = audio;
        audio.onended = () => setIsSpeaking(false);
        audio.play();
      }
      
    } catch (error) {
      console.error('Voice processing error:', error);
      const errorMessage = {
        id: Date.now(),
        role: 'assistant',
        content: 'Sorry, I had trouble processing that. Please try again.',
        timestamp: new Date().toISOString()
      };
      setVoiceMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsProcessing(false);
    }
  };

  const stopSpeaking = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsSpeaking(false);
    }
  };

  const quickActions = [
    { label: 'New Chat', icon: Bot, screen: 'chat' },
    { label: 'Messenger', icon: MessageCircle, screen: 'messenger' },
    { label: 'Email', icon: Mail, screen: 'email' },
    { label: 'Games', icon: Gamepad2, screen: 'games' },
    { label: 'Finance', icon: DollarSign, screen: 'finance' },
    { label: 'More Tools', icon: Wrench, screen: 'tools' }
  ];

  const currentModel = VOICE_MODELS.find(m => m.id === selectedModel);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#08080c] flex items-center justify-center pb-20">
        <div className="text-[#00d4ff] font-mono text-sm animate-pulse">Loading...</div>
      </div>
    );
  }

  return (
    <div className="bg-[#08080c] pb-20 p-4" data-testid="home-screen">
      {/* Stats Grid - Feed, Calendar, Friends Online */}
      <div className="grid grid-cols-3 gap-3 mb-4" data-testid="stats-grid">
        <div onClick={() => navigate('social')} className="bg-[#0d0d12] border border-[#00d4ff]/30 p-3 cursor-pointer hover:border-[#00d4ff] hover:shadow-[0_0_15px_rgba(0,212,255,0.15)] transition-all" data-testid="stat-feed">
          <div className="flex items-center gap-2 mb-2">
            <Rss className="w-4 h-4 text-[#00d4ff]" />
            <p className="font-mono text-[9px] text-[#00d4ff] uppercase tracking-wider">Feed</p>
          </div>
          <p className="text-2xl font-light text-[#e8e8f0]">{stats.postsCount}</p>
          <p className="text-[9px] text-[#4a4a60]">posts today</p>
        </div>

        <div onClick={() => navigate('tools')} className="bg-[#0d0d12] border border-[#00d4ff]/30 p-3 cursor-pointer hover:border-[#00d4ff] hover:shadow-[0_0_15px_rgba(0,212,255,0.15)] transition-all" data-testid="stat-calendar">
          <div className="flex items-center gap-2 mb-2">
            <Calendar className="w-4 h-4 text-[#00d4ff]" />
            <p className="font-mono text-[9px] text-[#00d4ff] uppercase tracking-wider">Calendar</p>
          </div>
          <p className="text-2xl font-light text-[#e8e8f0]">{stats.upcomingEvents}</p>
          <p className="text-[9px] text-[#4a4a60]">upcoming</p>
        </div>

        <div onClick={() => setShowFriends(true)} className="bg-[#0d0d12] border border-[#00d4ff]/30 p-3 cursor-pointer hover:border-[#00d4ff] hover:shadow-[0_0_15px_rgba(0,212,255,0.15)] transition-all" data-testid="stat-friends">
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-4 h-4 text-[#00d4ff]" />
            <p className="font-mono text-[9px] text-[#00d4ff] uppercase tracking-wider">Friends</p>
          </div>
          <p className="text-2xl font-light text-[#e8e8f0]">{stats.friendsOnline}</p>
          <p className="text-[9px] text-[#4a4a60]">online</p>
        </div>
      </div>

      {/* Friends Modal */}
      {showFriends && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowFriends(false)}>
          <div className="bg-[#0d0d12] border border-[#00d4ff]/30 rounded-lg w-full max-w-sm" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-[#1a1a24]">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-[#00d4ff]" />
                <h3 className="font-mono text-xs text-[#00d4ff] uppercase tracking-wider">Friends Online</h3>
              </div>
              <button onClick={() => setShowFriends(false)} className="p-1 hover:bg-[#111118]"><X className="w-4 h-4 text-[#4a4a60]" /></button>
            </div>
            <div className="p-4 max-h-64 overflow-y-auto">
              {friendsList.length === 0 ? (
                <p className="text-[#4a4a60] text-xs text-center py-4">No friends online</p>
              ) : (
                <div className="space-y-2">
                  {friendsList.map(friend => (
                    <div key={friend.id} className="flex items-center gap-3 p-2 bg-[#08080c] border border-[#1a1a24] rounded">
                      <div className="w-8 h-8 bg-[#00d4ff]/20 rounded-full flex items-center justify-center text-[#00d4ff] text-xs font-bold">
                        {friend.username?.[0]?.toUpperCase() || '?'}
                      </div>
                      <div>
                        <p className="text-xs text-[#e8e8f0]">{friend.username}</p>
                        <div className="flex items-center gap-1">
                          <div className="w-2 h-2 bg-[#00ff88] rounded-full" />
                          <span className="text-[9px] text-[#00ff88]">Online</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Voice AI Chat Section with Model Selector */}
      <div className="mb-4" data-testid="voice-chat-section">
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-mono text-[10px] text-[#00d4ff] uppercase tracking-[0.2em]">Sage Voice Assistant</h2>
          
          {/* Model Selector */}
          <div className="relative">
            <button
              onClick={() => setShowModelPicker(!showModelPicker)}
              className="flex items-center gap-2 px-3 py-1.5 bg-[#0d0d12] border border-[#00d4ff]/30 rounded text-xs text-[#e8e8f0] hover:border-[#00d4ff] transition-all"
              data-testid="model-selector"
            >
              <Sparkles className="w-3 h-3 text-[#00d4ff]" />
              <span className="font-mono text-[10px] uppercase">{currentModel?.name}</span>
              <ChevronDown className={`w-3 h-3 text-[#4a4a60] transition-transform ${showModelPicker ? 'rotate-180' : ''}`} />
            </button>
            
            {showModelPicker && (
              <div className="absolute right-0 top-full mt-1 bg-[#0d0d12] border border-[#00d4ff]/30 rounded-lg z-20 min-w-[180px] overflow-hidden shadow-lg">
                {VOICE_MODELS.map(model => (
                  <button
                    key={model.id}
                    onClick={() => { setSelectedModel(model.id); setShowModelPicker(false); }}
                    className={`w-full px-3 py-2 text-left hover:bg-[#00d4ff]/10 transition-colors ${selectedModel === model.id ? 'bg-[#00d4ff]/20' : ''}`}
                    data-testid={`select-model-${model.id}`}
                  >
                    <p className="font-mono text-[10px] text-[#e8e8f0] uppercase">{model.name}</p>
                    <p className="text-[9px] text-[#4a4a60]">{model.description}</p>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        
        {/* Spruced Up Voice Chat Box */}
        <div className="bg-gradient-to-b from-[#0d0d12] to-[#08080c] border border-[#00d4ff]/40 rounded-xl overflow-hidden shadow-[0_0_30px_rgba(0,212,255,0.1)]">
          {/* Chat Messages */}
          <div className="h-32 overflow-y-auto p-3 space-y-2" data-testid="voice-messages">
            {voiceMessages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <div className="relative">
                  <div className="absolute inset-0 bg-[#00d4ff]/20 rounded-full blur-xl animate-pulse" />
                  <Mic className="relative w-8 h-8 text-[#00d4ff]/40" />
                </div>
                <p className="text-[#6a6a8a] text-xs font-mono mt-3">Tap to speak with Sage</p>
                <p className="text-[#4a4a60] text-[9px] mt-1">
                  {selectedModel === 'sage' ? 'Ask anything' : `${currentModel?.description}`}
                </p>
              </div>
            ) : (
              voiceMessages.map((msg) => (
                <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] px-3 py-2 rounded-lg ${
                    msg.role === 'user' 
                      ? 'bg-[#00d4ff]/20 border border-[#00d4ff]/30 text-[#e8e8f0]' 
                      : 'bg-[#1a1a24] border border-[#2a2a3a] text-[#c8c8d0]'
                  }`}>
                    <p className="text-[11px]">{msg.content}</p>
                  </div>
                </div>
              ))
            )}
            {isProcessing && (
              <div className="flex justify-start">
                <div className="bg-[#1a1a24] border border-[#2a2a3a] px-3 py-2 rounded-lg flex items-center gap-2">
                  <Loader2 className="w-3 h-3 text-[#00d4ff] animate-spin" />
                  <span className="text-[10px] text-[#4a4a60]">Processing...</span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Action Confirmation */}
          {lastAction && lastAction.success && (
            <div className="mx-3 mb-2 p-2 bg-[#00ff88]/10 border border-[#00ff88]/30 rounded flex items-center gap-2">
              <Check className="w-3 h-3 text-[#00ff88]" />
              <span className="text-[10px] text-[#00ff88]">
                {lastAction.type === 'task' && `Task created: ${lastAction.title}`}
                {lastAction.type === 'note' && `Note created: ${lastAction.title}`}
                {lastAction.type === 'event' && `Event scheduled: ${lastAction.title}`}
                {lastAction.type === 'message' && `Message sent to ${lastAction.recipient}`}
                {lastAction.type === 'channel_message' && `Message sent to #${lastAction.channel}`}
              </span>
            </div>
          )}

          {/* Voice Controls - Enhanced */}
          <div className="border-t border-[#00d4ff]/20 bg-[#0a0a0e] p-4">
            {/* Send Options Modal */}
            {showVoiceOptions && (
              <div className="mb-4 p-3 bg-[#111118] border border-[#00d4ff]/30 rounded-lg">
                <p className="text-[10px] text-[#4a4a60] text-center mb-3">Recording complete. What would you like to do?</p>
                <div className="flex gap-2">
                  <button onClick={sendVoiceMessage} className="flex-1 flex items-center justify-center gap-1 py-2 bg-[#00d4ff]/20 border border-[#00d4ff] text-[#00d4ff] text-[10px] rounded hover:bg-[#00d4ff]/30" data-testid="send-voice-btn">
                    <Send className="w-3 h-3" /> Send
                  </button>
                  <button onClick={cancelRecording} className="flex-1 flex items-center justify-center gap-1 py-2 border border-[#1a1a24] text-[#4a4a60] text-[10px] rounded hover:border-[#ff3366]/50 hover:text-[#ff3366]" data-testid="cancel-voice-btn">
                    <Trash2 className="w-3 h-3" /> Discard
                  </button>
                </div>
              </div>
            )}

            <div className="flex items-center justify-center gap-3">
              {isSpeaking ? (
                <button onClick={stopSpeaking} className="relative p-4 bg-[#ff3366]/20 border-2 border-[#ff3366] rounded-full hover:bg-[#ff3366]/30 transition-all group" data-testid="stop-speaking-btn">
                  <div className="absolute inset-0 bg-[#ff3366]/30 rounded-full blur-lg animate-pulse" />
                  <StopCircle className="relative w-6 h-6 text-[#ff3366]" />
                </button>
              ) : isRecording ? (
                <>
                  {/* Pause/Resume Button */}
                  <button onClick={isPaused ? resumeRecording : pauseRecording} className="p-3 bg-[#ffcc00]/20 border border-[#ffcc00]/50 rounded-full hover:bg-[#ffcc00]/30 transition-all" data-testid="pause-recording-btn">
                    {isPaused ? <Play className="w-5 h-5 text-[#ffcc00]" /> : <Pause className="w-5 h-5 text-[#ffcc00]" />}
                  </button>
                  
                  {/* Stop Button */}
                  <button onClick={stopRecording} className="relative p-4 bg-[#ff3366]/20 border-2 border-[#ff3366] rounded-full hover:bg-[#ff3366]/30 transition-all" data-testid="stop-recording-btn">
                    <div className="absolute inset-0 bg-[#ff3366]/40 rounded-full blur-xl animate-ping" />
                    <Square className="relative w-6 h-6 text-[#ff3366]" />
                  </button>
                  
                  {/* Cancel Button */}
                  <button onClick={cancelRecording} className="p-3 border border-[#1a1a24] rounded-full hover:border-[#4a4a60] transition-all" data-testid="cancel-recording-btn">
                    <X className="w-5 h-5 text-[#4a4a60]" />
                  </button>
                </>
              ) : (
                <button onClick={startRecording} disabled={isProcessing || showVoiceOptions} className="relative p-4 bg-[#00d4ff]/10 border-2 border-[#00d4ff] rounded-full hover:bg-[#00d4ff]/20 hover:shadow-[0_0_30px_rgba(0,212,255,0.4)] transition-all disabled:opacity-50 group" data-testid="start-recording-btn">
                  <div className="absolute inset-0 bg-[#00d4ff]/20 rounded-full blur-lg opacity-0 group-hover:opacity-100 transition-opacity" />
                  <Mic className="relative w-6 h-6 text-[#00d4ff]" />
                </button>
              )}
            </div>
            
            {/* Status Text */}
            <div className="text-center mt-3">
              {isSpeaking && <div className="flex items-center justify-center gap-2"><Volume2 className="w-4 h-4 text-[#00d4ff] animate-pulse" /><span className="text-[10px] text-[#00d4ff] font-mono">Speaking...</span></div>}
              {isRecording && !isPaused && <div className="flex items-center justify-center gap-2"><div className="w-2 h-2 bg-[#ff3366] rounded-full animate-pulse" /><span className="text-[10px] text-[#ff3366] font-mono">Recording...</span></div>}
              {isRecording && isPaused && <div className="flex items-center justify-center gap-2"><Pause className="w-3 h-3 text-[#ffcc00]" /><span className="text-[10px] text-[#ffcc00] font-mono">Paused</span></div>}
              {!isSpeaking && !isRecording && !isProcessing && !showVoiceOptions && <span className="text-[9px] text-[#4a4a60] font-mono">Tap to speak</span>}
            </div>

            {/* Save Chat History Button */}
            {voiceMessages.length > 0 && !isRecording && (
              <div className="flex justify-center gap-2 mt-3 pt-3 border-t border-[#1a1a24]">
                <button onClick={saveVoiceChatHistory} className="flex items-center gap-1 px-3 py-1.5 border border-[#00d4ff]/30 text-[#00d4ff] text-[9px] rounded hover:bg-[#00d4ff]/10" data-testid="save-chat-btn">
                  <Save className="w-3 h-3" /> Save to Notes
                </button>
                <button onClick={clearVoiceChat} className="flex items-center gap-1 px-3 py-1.5 border border-[#1a1a24] text-[#4a4a60] text-[9px] rounded hover:border-[#ff3366]/30 hover:text-[#ff3366]" data-testid="clear-chat-btn">
                  <Trash2 className="w-3 h-3" /> Clear
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="mb-4">
        <h2 className="font-mono text-[10px] text-[#00d4ff] uppercase tracking-[0.2em] mb-2">Quick Actions</h2>
        <div className="grid grid-cols-3 gap-2" data-testid="quick-actions">
          {quickActions.map((action) => (
            <button key={action.label} onClick={() => navigate(action.screen)} className="bg-[#0d0d12] border border-[#00d4ff]/30 p-3 flex flex-col items-center gap-2 hover:border-[#00d4ff] hover:shadow-[0_0_15px_rgba(0,212,255,0.15)] transition-all group rounded-lg" data-testid={`quick-action-${action.label.toLowerCase().replace(' ', '-')}`}>
              <action.icon className="w-5 h-5 text-[#00d4ff] group-hover:drop-shadow-[0_0_8px_rgba(0,212,255,0.6)] transition-all" />
              <span className="font-mono text-[9px] text-[#e8e8f0] uppercase tracking-wider">{action.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Tasks Section */}
      <div className="mb-4" data-testid="tasks-section">
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-mono text-[10px] text-[#00d4ff] uppercase tracking-[0.2em]">Open Tasks</h2>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowAddTask(!showAddTask)} className="p-1 border border-[#00d4ff]/30 rounded hover:border-[#00d4ff] hover:bg-[#00d4ff]/10 transition-all" data-testid="add-task-btn">
              <Plus className="w-3 h-3 text-[#00d4ff]" />
            </button>
            <button onClick={() => navigate('tools')} className="text-[9px] text-[#4a4a60] hover:text-[#00d4ff] font-mono">View All</button>
          </div>
        </div>
        <div className="bg-[#0d0d12] border border-[#00d4ff]/30 rounded-lg p-3">
          {/* Quick Add Task Form */}
          {showAddTask && (
            <div className="flex gap-2 mb-3 pb-3 border-b border-[#1a1a24]">
              <input
                type="text"
                value={newTaskTitle}
                onChange={(e) => setNewTaskTitle(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleQuickAddTask()}
                placeholder="New task..."
                className="flex-1 bg-[#08080c] border border-[#1a1a24] text-[#e8e8f0] rounded px-2 py-1.5 text-[11px] focus:border-[#00d4ff]/50 outline-none"
                autoFocus
                data-testid="new-task-input"
              />
              <button onClick={handleQuickAddTask} className="px-3 bg-[#00d4ff]/20 border border-[#00d4ff]/50 text-[#00d4ff] text-[10px] rounded hover:bg-[#00d4ff]/30">Add</button>
            </div>
          )}
          {tasks.length === 0 && !showAddTask ? (
            <div className="text-center py-3">
              <CheckSquare className="w-5 h-5 text-[#2a2a3a] mx-auto mb-1" />
              <p className="text-[#4a4a60] text-[10px] font-mono">No open tasks</p>
            </div>
          ) : (
            <div className="space-y-2">
              {tasks.map(task => (
                <div key={task.id} className="flex items-center gap-2 p-2 bg-[#08080c] border border-[#1a1a24] rounded group">
                  <button onClick={() => toggleTask(task.id, task.completed)} className="text-[#4a4a60] hover:text-[#00ff88]">
                    <CheckSquare className="w-4 h-4" />
                  </button>
                  <span className="flex-1 text-[11px] text-[#e8e8f0] truncate">{task.title}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Notes Section */}
      <div className="mb-4" data-testid="notes-section">
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-mono text-[10px] text-[#00d4ff] uppercase tracking-[0.2em]">Recent Notes</h2>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowAddNote(!showAddNote)} className="p-1 border border-[#00d4ff]/30 rounded hover:border-[#00d4ff] hover:bg-[#00d4ff]/10 transition-all" data-testid="add-note-btn">
              <Plus className="w-3 h-3 text-[#00d4ff]" />
            </button>
            <button onClick={() => navigate('tools')} className="text-[9px] text-[#4a4a60] hover:text-[#00d4ff] font-mono">View All</button>
          </div>
        </div>
        {/* Quick Add Note Form */}
        {showAddNote && (
          <div className="mb-2 p-3 bg-[#0d0d12] border border-[#00d4ff]/30 rounded-lg">
            <textarea
              value={newNoteContent}
              onChange={(e) => setNewNoteContent(e.target.value)}
              placeholder="Quick note..."
              className="w-full bg-[#08080c] border border-[#1a1a24] text-[#e8e8f0] rounded px-2 py-1.5 text-[11px] focus:border-[#00d4ff]/50 outline-none resize-none"
              rows={2}
              autoFocus
              data-testid="new-note-input"
            />
            <div className="flex gap-2 mt-2">
              <button onClick={handleQuickAddNote} className="flex-1 bg-[#00d4ff]/20 border border-[#00d4ff]/50 text-[#00d4ff] text-[10px] py-1 rounded hover:bg-[#00d4ff]/30">Save Note</button>
              <button onClick={() => setShowAddNote(false)} className="px-3 border border-[#1a1a24] text-[#4a4a60] text-[10px] py-1 rounded">Cancel</button>
            </div>
          </div>
        )}
        <div className="grid grid-cols-2 gap-2">
          {notes.length === 0 && !showAddNote ? (
            <div className="col-span-2 bg-[#0d0d12] border border-[#00d4ff]/30 rounded-lg p-3 text-center">
              <FileText className="w-5 h-5 text-[#2a2a3a] mx-auto mb-1" />
              <p className="text-[#4a4a60] text-[10px] font-mono">No notes yet</p>
            </div>
          ) : (
            notes.map(note => (
              <div key={note.id} onClick={() => { setEditingNote(note.id); setNoteEditText(note.content); }} className="bg-[#0d0d12] border border-[#00d4ff]/30 rounded-lg p-2 cursor-pointer hover:border-[#00d4ff] transition-all">
                {editingNote === note.id ? (
                  <div onClick={e => e.stopPropagation()}>
                    <textarea value={noteEditText} onChange={(e) => setNoteEditText(e.target.value)} className="w-full bg-[#08080c] border border-[#1a1a24] p-1.5 text-[10px] text-[#e8e8f0] rounded resize-none h-16" autoFocus />
                    <div className="flex gap-1 mt-1">
                      <button onClick={() => updateNote(note.id, noteEditText)} className="flex-1 bg-[#00d4ff]/20 border border-[#00d4ff]/50 text-[#00d4ff] text-[9px] py-1 rounded">Save</button>
                      <button onClick={() => setEditingNote(null)} className="px-2 border border-[#1a1a24] text-[#4a4a60] text-[9px] py-1 rounded">X</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <p className="text-[10px] text-[#e8e8f0] line-clamp-2">{note.content || note.title}</p>
                    <p className="text-[8px] text-[#4a4a60] mt-1">{new Date(note.updated_at).toLocaleDateString()}</p>
                  </>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Calendar Section */}
      <div className="mb-4" data-testid="calendar-section">
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-mono text-[10px] text-[#00d4ff] uppercase tracking-[0.2em]">Upcoming Events</h2>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowAddEvent(!showAddEvent)} className="p-1 border border-[#00d4ff]/30 rounded hover:border-[#00d4ff] hover:bg-[#00d4ff]/10 transition-all" data-testid="add-event-btn">
              <Plus className="w-3 h-3 text-[#00d4ff]" />
            </button>
            <button onClick={() => navigate('tools')} className="text-[9px] text-[#4a4a60] hover:text-[#00d4ff] font-mono">View All</button>
          </div>
        </div>
        <div className="bg-[#0d0d12] border border-[#00d4ff]/30 rounded-lg p-3">
          {/* Quick Add Event Form */}
          {showAddEvent && (
            <div className="mb-3 pb-3 border-b border-[#1a1a24] space-y-2">
              <input
                type="text"
                value={newEventTitle}
                onChange={(e) => setNewEventTitle(e.target.value)}
                placeholder="Event title..."
                className="w-full bg-[#08080c] border border-[#1a1a24] text-[#e8e8f0] rounded px-2 py-1.5 text-[11px] focus:border-[#00d4ff]/50 outline-none"
                autoFocus
                data-testid="new-event-title"
              />
              <input
                type="date"
                value={newEventDate}
                onChange={(e) => setNewEventDate(e.target.value)}
                className="w-full bg-[#08080c] border border-[#1a1a24] text-[#e8e8f0] rounded px-2 py-1.5 text-[11px] focus:border-[#00d4ff]/50 outline-none"
                data-testid="new-event-date"
              />
              <div className="flex gap-2">
                <button onClick={handleQuickAddEvent} className="flex-1 bg-[#00d4ff]/20 border border-[#00d4ff]/50 text-[#00d4ff] text-[10px] py-1 rounded hover:bg-[#00d4ff]/30">Add Event</button>
                <button onClick={() => setShowAddEvent(false)} className="px-3 border border-[#1a1a24] text-[#4a4a60] text-[10px] py-1 rounded">Cancel</button>
              </div>
            </div>
          )}
          {events.length === 0 && !showAddEvent ? (
            <div className="text-center py-3">
              <Calendar className="w-5 h-5 text-[#2a2a3a] mx-auto mb-1" />
              <p className="text-[#4a4a60] text-[10px] font-mono">No upcoming events</p>
            </div>
          ) : (
            <div className="space-y-2">
              {events.map(event => (
                <div key={event.id} className="flex items-center gap-2 p-2 bg-[#08080c] border border-[#1a1a24] rounded">
                  <Clock className="w-4 h-4 text-[#00d4ff]" />
                  <span className="flex-1 text-[11px] text-[#e8e8f0] truncate">{event.title}</span>
                  <span className="text-[9px] text-[#4a4a60]">{new Date(event.due_date).toLocaleDateString()}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Sage Owl Branding */}
      <div className="mt-4 flex flex-col items-center opacity-50 hover:opacity-70 transition-opacity">
        <img src="/owl-logo.png" alt="Sage" className="w-10 h-10 mb-1" style={{ filter: 'drop-shadow(0 0 10px rgba(0, 212, 255, 0.4))' }} />
        <span className="font-mono text-[9px] text-[#00d4ff] tracking-[0.3em]">SAGE</span>
      </div>
    </div>
  );
}
